import React from 'react';
import { Link } from 'react-router-dom';

const formatDateRange = (startDate, endDate) => {
  if (!startDate) return 'TBD';
  const start = new Date(startDate);
  const startFmt = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  if (!endDate || endDate === startDate) return startFmt;
  const end = new Date(endDate);
  const endFmt = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  return `${startFmt} – ${endFmt}`;
};

const getDaysToGo = (dateString) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const eventDate = new Date(dateString);
  eventDate.setHours(0, 0, 0, 0);
  const diffTime = eventDate - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays < 0) return null;
  if (diffDays === 0) return 'Today!';
  if (diffDays === 1) return '1 day away';
  return `${diffDays} days away`;
};

const EventCard = ({ event }) => {
  const daysToGo = getDaysToGo(event.start_date);
  const venueName = event.venues?.name || event.venue_name;
  const venueCity = event.venues?.city || event.venues?.state
    ? [event.venues?.city, event.venues?.state].filter(Boolean).join(', ')
    : event.location;

  return (
    <Link
      to={`/events/${event.id}`}
      className="group block bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
    >
      {/* Date header */}
      <div className="bg-gradient-to-r from-primary to-primary-dark p-4 text-white">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-3xl font-bold">
              {new Date(event.start_date).getDate()}
            </p>
            <p className="text-sm opacity-90">
              {new Date(event.start_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
            </p>
          </div>
          {daysToGo && (
            <div className="text-right">
              <span className="inline-block px-3 py-1 bg-white/20 rounded-full text-xs font-semibold">
                {daysToGo}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Details */}
      <div className="p-4">
        <h3 className="font-bold text-gray-900 dark:text-white group-hover:text-primary transition-colors line-clamp-2">
          {event.title}
        </h3>

        <div className="mt-3 space-y-2">
          {venueName && (
            <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
              <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              <span className="truncate">{venueName}</span>
            </div>
          )}

          {venueCity && (
            <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
              <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span className="truncate">{venueCity}</span>
            </div>
          )}

          {event.end_date && event.end_date !== event.start_date && (
            <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
              <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span>{formatDateRange(event.start_date, event.end_date)}</span>
            </div>
          )}
        </div>

        <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-700">
          <span className="text-sm font-medium text-primary group-hover:underline">
            View Conference →
          </span>
        </div>
      </div>
    </Link>
  );
};

const SpeakerEvents = ({ upcomingEvents, pastEvents }) => {
  return (
    <>
      {/* Upcoming Conferences */}
      {upcomingEvents && upcomingEvents.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Upcoming Conferences
              <span className="ml-2 text-sm font-normal text-gray-500">({upcomingEvents.length})</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {upcomingEvents.map((event) => (
              <EventCard key={event.id} event={event} />
            ))}
          </div>
        </div>
      )}

      {/* Past Conferences */}
      {pastEvents && pastEvents.length > 0 && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Past Conferences
              <span className="ml-2 text-sm font-normal text-gray-500">({pastEvents.length})</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 opacity-75">
            {pastEvents.map((event) => (
              <EventCard key={event.id} event={event} />
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default SpeakerEvents;
